import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { DialogBaseComponent } from '../../common/dialog/dialog-base/dialog-base.component';
import { LoadingService } from 'src/app/services/UI/loading.service';
import { DialogService } from 'src/app/services/UI/dialog.service';
import { TranslateHandler } from 'src/app/common/helpers/translate-handler';
import { MessageDialogService } from '../../common/message-dialog/services/message-dialog.service';
import { UserProfileService } from 'src/app/services/API/userprofile/user-profile.service';
import { IUserProfile } from '../models/user-profile.model';
import { ResponseModel } from 'src/app/common/models/ResponseModel';
import { GuidGenerator } from 'src/app/common/helpers/guid-generator';
import { UserUpdateService } from 'src/app/services/user-update.service';
import { AuthenticationService } from 'src/app/services/authentication.service';

@Component({
  selector: 'app-user-profile-edit',
  templateUrl: './user-profile-edit.component.html',
  styleUrls: ['./user-profile-edit.component.scss'],
  standalone: false
})
export class UserProfileEditComponent extends DialogBaseComponent implements OnInit {
  @ViewChild('productsTextarea') productsTextarea!: ElementRef<HTMLTextAreaElement>;
  @Input() data;

  profile: IUserProfile = {} as IUserProfile;
  originalProfile: IUserProfile = {} as IUserProfile;
  isFormDirty = false;
  isEditing = false;
  isPasswordMismatch = false;
  password = {
    current: '',
    new: '',
    confirm: ''
  };

  policy = {
    minLength: 8,
    uppercase: 1,
    numeric: 1,
    special: 1
  };

  errors = {
    minLength: false,
    uppercase: false,
    numeric: false,
    special: false,
    sameAsUsername: false,
    sameAsOldPassword: false
  };

  passwordFieldTypes = {
    current: 'password',
    new: 'password',
    confirm: 'password'
  };

  showPasswordForm = false;
  productsText: string = '';
  profileSaveSuccess = false;
  passwordSaveSuccess = false;
  incorrectPassword = false;
  unknownError = false;

  constructor(private loadingService: LoadingService,
    dialogService: DialogService,
    private translateHandler: TranslateHandler,
    private messageDialogService: MessageDialogService,
    ref: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private userProfileService: UserProfileService,
    private userUpdateService: UserUpdateService) {
    super(dialogService, ref);
  }

  ngOnInit(): void {
    this.loadUserData();
  }

  loadUserData() {
    let processId = GuidGenerator.generate();
    this.loadingService.show(processId);
    this.userProfileService.getProfileDetails().subscribe(
      (res: ResponseModel<IUserProfile>) => {
        this.profile = res.data;
        this.originalProfile = { ...res.data };
        this.checkDirty();

        const productsArray = this.profile.purchasedProducts
          ? this.profile.purchasedProducts.split(';').filter(p => p.trim() !== '')
          : [];

        this.productsText = productsArray.join('\n');
        setTimeout(() => {
          this.autoResizeTextarea();
        });
        this.loadingService.hide(processId);
      }
    );
  }

  checkDirty() {
    const isDirty =
      this.profile.firstName !== this.originalProfile.firstName ||
      this.profile.middleName !== this.originalProfile.middleName ||
      this.profile.lastName !== this.originalProfile.lastName;
    const hasRequiredFields = !!this.profile.firstName && !!this.profile.lastName;

    this.isFormDirty = isDirty && hasRequiredFields;

  }

  saveUser() {
    let processId = GuidGenerator.generate();
    this.loadingService.show(processId);
    this.userProfileService.updateProfile(this.profile).subscribe({
      next: () => {
        this.authenticationService.setCookieUserName(this.profile.firstName, this.profile.lastName);
        this.userUpdateService.updateUserName(this.profile);
        this.loadingService.hide(processId);
        this.originalProfile = { ...this.profile };
        this.isFormDirty = false;
        this.profileSaveSuccess = true;
        setTimeout(() => this.profileSaveSuccess = false, 6000);
      },
      error: (err) => {
        this.loadingService.hide(processId);
        this.originalProfile = { ...this.profile };
        this.isFormDirty = false;
        this.unknownError = true;
        setTimeout(() => this.unknownError = false, 6000);
        this.loadUserData();
      }
    });
  }

  togglePassword() {
    this.showPasswordForm = !this.showPasswordForm;
    this.password = { current: '', new: '', confirm: '' };
    this.errors = {
      minLength: false,
      uppercase: false,
      numeric: false,
      special: false,
      sameAsUsername: false,
      sameAsOldPassword: false
    };

    this.isPasswordMismatch = false;
    this.incorrectPassword = false;
    this.unknownError = false;
  }

  changePassword() {
    const payload = {
      oldPassword: this.password.current,
      newPassword: this.password.new
    };
    let processId = GuidGenerator.generate();
    this.loadingService.show(processId);
    this.userProfileService.updatePassword(payload).subscribe({
      next: (res) => {
        this.loadingService.hide(processId);
        if (res.success) {
          this.password = { current: '', new: '', confirm: '' };
          this.incorrectPassword = false;
          this.unknownError = false;
          this.passwordSaveSuccess = true;
          setTimeout(() => {
            this.passwordSaveSuccess = false;
            this.showPasswordForm = false;
          }, 6000);
        }
      },
      error: (err) => {
        this.loadingService.hide(processId);
        if (err.status === 400 && err.error?.isPasswordMatched === false) {
          this.incorrectPassword = true;
        }
        else {
          this.unknownError = true;
        }
      }
    });
  }

  cancel() {
    this.dialogSerivce.complete({ status: 'success', firstName: this.profile.firstName, lastName: this.profile.lastName });
  }

  validatePassword() {
    const pwd = this.password.new || '';
    const username = this.profile.firstName || '';

    this.errors.minLength = pwd.length < this.policy.minLength;
    this.errors.uppercase = !/[A-Z]/.test(pwd);
    this.errors.numeric = !/[0-9]/.test(pwd);
    this.errors.special = !/[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    this.errors.sameAsUsername = pwd.toLowerCase() === username.toLowerCase();
    this.errors.sameAsOldPassword = this.password.new === this.password.current;

    if (this.password.confirm?.trim() !== '') {
      this.validatePasswordMismatch();
    }
  }

  validatePasswordMatchOldNew() {
    this.errors.sameAsOldPassword = this.password.new === this.password.current;
  }

  validatePasswordMismatch() {
    if (this.password.new !== this.password.confirm) {
      this.isPasswordMismatch = true;
    }
    else {
      this.isPasswordMismatch = false;
    }
  }

  hasErrors(): boolean {
    return Object.values(this.errors).includes(true);
  }

  get passwordErrorsList(): string[] {
    const list: string[] = [];
    if (this.errors.minLength)
      list.push(this.translateHandler.translate("user_profile.lbl_password_policy.min_length_of_password", [this.policy.minLength.toString()]));

    if (this.errors.uppercase)
      list.push(this.translateHandler.translate("user_profile.lbl_password_policy.min_uppercase_chars_in_password", [this.policy.uppercase.toString()]));

    if (this.errors.numeric)
      list.push(this.translateHandler.translate("user_profile.lbl_password_policy.min_numeric_chars_in_password", [this.policy.numeric.toString()]));

    if (this.errors.special)
      list.push(this.translateHandler.translate("user_profile.lbl_password_policy.min_special_chars_in_password", [this.policy.special.toString()]));

    return list;
  }

  get passwordRestrictionErrors(): string[] {
    const list: string[] = [];

    if (this.errors.sameAsUsername)
      list.push(this.translateHandler.translate(
        "user_profile.lbl_password_policy.not_similar_as_username"
      ));

    if (this.errors.sameAsOldPassword)
      list.push(this.translateHandler.translate(
        "user_profile.lbl_password_policy.not_similar_as_current_password"
      ));

    return list;
  }

  get passwordErrorMessage(): string {
    const parts: string[] = [];

    if (this.passwordErrorsList.length) {
      parts.push(
        `${this.translateHandler.translate(
          "user_profile.lbl_password_policy.prefix"
        )} ${this.passwordErrorsList.join(', ')}`
      );
    }

    if (this.passwordRestrictionErrors.length) {
      parts.push(this.passwordRestrictionErrors.join(', '));
    }

    return parts.join(', ');
  }

  get canSavePassword(): boolean {
    const pwd = this.password.new;
    const confirm = this.password.confirm;
    const current = this.password.current;

    // All fields must be filled
    if (!current || !pwd || !confirm) return false;

    // Password must have no errors
    if (this.hasErrors()) return false;

    // Password and confirm must match
    if (this.isPasswordMismatch) return false;

    return true;
  }

  togglePasswordVisibility(field: 'current' | 'new' | 'confirm') {
    this.passwordFieldTypes[field] =
      this.passwordFieldTypes[field] === 'password' ? 'text' : 'password';
  }

  private autoResizeTextarea() {
    const textarea = this.productsTextarea.nativeElement;
    textarea.style.height = 'auto';                
    textarea.style.height = textarea.scrollHeight + 'px';
  }
}
